import React, { useEffect, useMemo, useState } from 'react'
import classes from './TableArea.module.scss'
// import Checkbox from "@material-ui/core/Checkbox";
import Checkbox from '@mui/material/Checkbox'

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  getFilteredRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table'
import {
  TiArrowSortedUp,
  TiArrowSortedDown,
  TiArrowUnsorted,
} from 'react-icons/ti'
import { useMediaQuery, useTheme } from '@material-ui/core'
import EditIcon from '@mui/icons-material/Edit' // 必要に応じてアイコンライブラリをインポート
import Pagination from './Pagination'
import {
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from '../../../constants/orderStatus'
import { EditOrderModal } from '../../UIkit/modal/EditOrderModal'
import { ColorButtons } from '../../UIkit'
import { formatOrderDate } from '../../../functions/utils/formatting'
import {
  ORDER_DATE_FIELDS,
  toDateOrNull,
} from '../../../functions/order/orderDateUpdate'
import { createOrderList } from '../../../functions/order/createOrderList'

const TableArea = (props) => {
  const theme = useTheme()
  const {
    orders: initialOrders,
    filteredOrders,
    setFilteredOrders,
    initialSortField,
  } = props
  const [orders, setOrders] = useState(
    initialOrders.map((order) => ({ ...order, isChecked: false }))
  )
  // 再取得時もチェック状態を注文IDで引き継ぐ。
  // Main.jsx が orders コレクション全体を購読しているため、他の担当者が1件注文しただけでも
  // ここが再実行される。以前は毎回すべて解除していたので、
  //   ・一覧で複数件を選んでいる最中に選択が消える
  //   ・確認ダイアログを開いている間に選択が空になり、入力した日付ごと操作が失われる
  // という状態だった。データ自体は最新のものに差し替わるので、
  // 選択中に他者がステータスを変えた場合も最新の状態で遷移可否が判定される。
  useEffect(() => {
    setOrders((prevOrders) => {
      const checkedIds = new Set(
        prevOrders.filter((order) => order.isChecked).map((order) => order.id)
      )
      return initialOrders.map((order) => ({
        ...order,
        isChecked: checkedIds.has(order.id),
      }))
    })
  }, [initialOrders])
  const isIphone = useMediaQuery('(max-width:600px)')
  const isMobile = useMediaQuery('(max-width:960px)')
  // 列のサイズ状態を追加
  const [columnSizing, setColumnSizing] = useState({})
  // 検索パネルで選んだ日付種類の列を初期ソート(降順)にする。
  // データ自体は取得時にソート済みだが、列ヘッダにソート状態が見えることで
  // ユーザーがそこから並べ替えを変更できる
  const [sorting, setSorting] = useState(() =>
    initialSortField ? [{ id: initialSortField, desc: true }] : []
  )
  const [globalFilter, setGlobalFilter] = useState('')
  const [pageSize, setPageSize] = useState(15)
  const [pageIndex, setPageIndex] = useState(0)
  const [columnResizeMode] = useState('onChange') // 追加
  const [columnResizeDirection] = useState('ltr') // 追加
  const [columnSizingInfo, setColumnSizingInfo] = useState({})
  const [isShowEdit, setIsShowEdit] = useState(false)
  const [orderId, setOrderId] = useState(null)
  const [orderUid, setOrderUid] = useState(null)
  const [targetOrders, setTargetOrders] = useState([])
  const [targetOrderList, setTargetOrderList] = useState([])
  const [columnFilters, setColumnFilters] = useState([])

  // 検索パネルの日付種類の選択が変わったら、その列の降順に並べ替えを合わせる
  // (手動の並べ替えより「選んだ日付で見る」という直近の意図を優先する)
  useEffect(() => {
    if (initialSortField) {
      setSorting([{ id: initialSortField, desc: true }])
    }
  }, [initialSortField])

  const handleEditClick = (order_id, order_uid) => {
    setOrderId(order_id)
    setOrderUid(order_uid)
    const filteredOrders = orders.filter((order) => order.order_id === order_id)
    setTargetOrderList(createOrderList(filteredOrders))
    setTargetOrders(filteredOrders)
    setIsShowEdit(true)
  }
  const handleClose = () => {
    setIsShowEdit(false)
    setOrderId(null)
    setOrderUid(null)
    setTargetOrders([])
    setTargetOrderList([])
  }

  // チェックボックスの状態を更新
  const handleCheckBoxClick = (orderId) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === orderId ? { ...order, isChecked: !order.isChecked } : order
      )
    )
  }
  const columns = useMemo(() => {
    // 通常時のカラム定義
    return [
      {
        id: 'edit',
        header: '',
        cell: ({ row }) => (
          <button
            className={classes.icon}
            onClick={() =>
              handleEditClick(row.original.order_id, row.original.id)
            }
          >
            <EditIcon style={{ color: theme.palette.grey['600'] }} />
          </button>
        ),
        enableSorting: false,
        enableColumnFilter: false, // フィルタリングを無効化
        size: 50,
      },
      {
        id: 'checkbox',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            indeterminate={table.getIsSomePageRowsSelected()}
            onChange={(e) => {
              const isChecked = e.target.checked
              const pageRows = table.getRowModel().rows
              setOrders((prevOrders) =>
                prevOrders.map((order) => {
                  const isInCurrentPage = pageRows.some(
                    (row) => row.original.id === order.id
                  )
                  if (isInCurrentPage) {
                    return { ...order, isChecked: isChecked }
                  } else {
                    return order
                  }
                })
              )
            }}
            inputProps={{ 'aria-label': 'Select all rows on current page' }}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.original.isChecked}
            onChange={() => handleCheckBoxClick(row.original.id)}
            inputProps={{ 'aria-label': 'Select row' }}
          />
        ),
        enableSorting: false,
        enableColumnFilter: false, // フィルタリングを無効化
        size: 50,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: '状態',
        cell: ({ getValue }) => (
          <div className={classes.statusName}>{getValue()}</div>
        ),
        size: 80,
      },
      {
        id: 'order_number',
        accessorKey: 'order_number',
        header: '注文番号',
        cell: ({ getValue }) => (
          <div className={classes.orderId}>{getValue()}</div>
        ),
        // maxSize は th/td の max-width に反映される。
        // 画面が広いとテーブルが引き伸ばされて列に余白が生まれるため、
        // 番号・日付は上限を決めて余った幅は商品名や備考に回す。
        // 96px は見出し「注文番号」(14px×4=56) ＋ 並べ替えアイコン ＋ パディングの実測値に合わせたもの。
        // これより狭めると見出しが折り返す(日付列と同じ幅なので見た目も揃う)。
        size: 96,
        maxSize: 96,
      },
      // 注文日・発注日・検収日・取消日(業務フロー順)。
      // accessorFn で "YYYY-MM-DD" の文字列にしてから渡すことで、
      // 並べ替えとフィルタが Timestamp オブジェクトのままでも正しく効くようにしている
      // (ISO形式なので辞書順 = 日付順)。
      ...ORDER_DATE_FIELDS.map(({ field, label }) => ({
        id: field,
        header: label,
        // ソート・フィルタ用の値は "YYYY-MM-DD"(ISO形式なので辞書順=日付順)。
        // 未設定は "—" ではなく undefined にし、sortUndefined で昇順・降順
        // どちらでも末尾に寄せる("—" は文字コードが数字より大きく、
        // 降順にすると未設定行が先頭へ来てしまうため)。表示の "—" は cell 側で付ける
        accessorFn: (row) =>
          toDateOrNull(row[field]) ? formatOrderDate(row[field]) : undefined,
        sortUndefined: 'last',
        cell: ({ row }) => (
          <div className={classes.date}>{formatOrderDate(row.original[field])}</div>
        ),
        size: 96,
        maxSize: 96,
      })),
      {
        id: 'shop_name',
        accessorKey: 'shop_name',
        header: '販売店',
        cell: ({ getValue }) => (
          <div className={classes.shopName}>{getValue()}</div>
        ),
      },
      {
        id: 'category_name',
        accessorKey: 'category_name',
        header: 'カテゴリ',
        cell: ({ getValue }) => (
          <div className={classes.categoryName}>{getValue()}</div>
        ),
      },
      {
        id: 'name',
        accessorKey: 'name',
        header: '商品名',
        cell: ({ getValue }) => (
          <div className={classes.productName}>{getValue()}</div>
        ),
      },
      {
        id: 'price',
        accessorKey: 'price',
        header: '単価',
        cell: ({ getValue }) => (
          <div className={classes.price}>
            {Number(getValue()).toLocaleString()}
          </div>
        ),
        filterFn: (row, columnId, filterValue) => {
          const rowValue = row.getValue(columnId)
          const filterValueNumber = Number(filterValue.replace(/,/g, ''))
          console.log('filterValueNumber', filterValueNumber, rowValue)
          return String(rowValue).includes(filterValue)
        },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: '数量',
        cell: ({ getValue }) => (
          <div className={classes.quantity}>
            {Number(getValue()).toLocaleString()}
          </div>
        ),
      },
      {
        id: 'totalPrice',
        accessorKey: 'totalPrice',
        header: '金額',
        cell: ({ getValue }) => (
          <div className={classes.totalPrice}>
            {Number(getValue()).toLocaleString()}
          </div>
        ),
        filterFn: (row, columnId, filterValue) => {
          const rowValue = row.getValue(columnId)
          const filterValueNumber = Number(filterValue.replace(/,/g, ''))
          console.log('filterValueNumber', filterValueNumber, rowValue)
          return String(rowValue).includes(filterValue)
        },
      },
      {
        id: 'deduction_name',
        accessorKey: 'deduction_name',
        header: '給与控除名',
        cell: ({ getValue }) => (
          <div className={classes.deductionName}>{getValue()}</div>
        ),
      },
      {
        id: 'deduction_price',
        accessorKey: 'deduction_price',
        header: '給与控除額',
        cell: ({ getValue }) => (
          <div className={classes.deductionPrice}>
            {Number(getValue()).toLocaleString()}
          </div>
        ),
        filterFn: (row, columnId, filterValue) => {
          const rowValue = row.getValue(columnId)
          const filterValueNumber = Number(filterValue.replace(/,/g, ''))
          console.log('filterValueNumber', filterValueNumber, rowValue)
          return String(rowValue).includes(filterValue)
        },
      },
      {
        id: 'billingUser',
        accessorKey: 'billingUser',
        header: '購入依頼者',
        accessorFn: (row) =>
          `${row.billing_user_id ? row.billing_user_id : ''} ${
            row.billing_user_name ? row.billing_user_name : ''
          }`,
        cell: ({ getValue }) => (
          <div className={classes.billingUser}>{getValue()}</div>
        ),
      },
      {
        id: 'billingCompany',
        accessorKey: 'billingCompany',
        header: '購入企業',
        accessorFn: (row) =>
          `${row.billing_company_id ? row.billing_company_id : ''} ${
            row.billing_company_name ? row.billing_company_name : ''
          }`,
        cell: ({ getValue }) => (
          <div className={classes.billingUser}>{getValue()}</div>
        ),
      },

      {
        id: 'orderUser',
        accessorKey: 'orderUser',
        header: '発注担当者',
        accessorFn: (row) => `${row.order_user_id} ${row.order_user_name}`,
        cell: ({ getValue }) => (
          <div className={classes.orderUser}>{getValue()}</div>
        ),
      },
      {
        id: 'orderDepartment',
        header: '発注部署',
        accessorFn: (row) =>
          `${row.order_base_name} ${row.order_department_name}`,
        cell: ({ getValue }) => (
          <div className={classes.orderDepartment}>{getValue()}</div>
        ),
      },
      {
        id: 'deliveryOffice',
        header: '納入先',
        accessorFn: (row) =>
          `${row.delivery_office_name} ${row.delivery_department}`,
        cell: ({ getValue }) => (
          <div className={classes.deliveryOffice}>{getValue()}</div>
        ),
      },
      {
        id: 'remarks',
        header: '備考',
        accessorFn: (row) => `${row.remarks}`,
        cell: ({ getValue }) => (
          <div className={classes.remarks}>{getValue()}</div>
        ),
      },
    ]
  }, [handleEditClick, handleCheckBoxClick])

  const table = useReactTable({
    data: orders,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination: {
        pageSize,
        pageIndex,
      },
      columnSizing,
      columnSizingInfo,
      columnFilters, // ここを追加
    },
    columnResizeMode,
    columnResizeDirection,
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function'
          ? updater({ pageSize, pageIndex })
          : updater
      setPageSize(newPagination.pageSize)
      // setPageIndex(newPagination.pageIndex);
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onColumnSizingChange: setColumnSizing,
    onColumnSizingInfoChange: setColumnSizingInfo,
    onColumnFiltersChange: setColumnFilters, // ここを追加
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
  })
  // useEffect(() => {
  //   const filteredRows = table.getFilteredRowModel().rows;
  //   const filteredData = filteredRows.map((row) => row.original);
  //   setFilteredOrders(filteredData);
  // }, [
  //   table.getState().globalFilter,
  //   table.getState().columnFilters,
  //   orders,
  //   setFilteredOrders,
  // ]);
  useEffect(() => {
    const filteredRows = table.getFilteredRowModel().rows
    const filteredData = filteredRows.map((row) => row.original)
    setFilteredOrders(filteredData)

    // フィルタ後のデータが現在のページに収まらない場合にページをリセット
    const pageCount = Math.ceil(filteredData.length / pageSize)

    // フィルタ結果が1ページだけの場合は1ページ目に移動
    if (pageIndex >= pageCount) {
      setPageIndex(pageCount - 1)
      if (pageCount === 0) {
        setPageIndex(0)
      }
    } else if (pageCount === 1) {
      setPageIndex(0)
    }
  }, [
    table.getState().globalFilter,
    table.getState().columnFilters,
    orders,
    setFilteredOrders,
    pageIndex, // 現在のページインデックスも監視
    pageSize, // ページサイズの変更も考慮
  ])

  table.getIsAllPageRowsSelected = () => {
    const pageRows = table.getRowModel().rows
    return pageRows.every((row) => row.original.isChecked)
  }

  table.getIsSomePageRowsSelected = () => {
    const pageRows = table.getRowModel().rows
    const someSelected = pageRows.some((row) => row.original.isChecked)
    const allSelected = table.getIsAllPageRowsSelected()
    return someSelected && !allSelected
  }
  useEffect(() => {
    // 新しいデータに基づいて最大ページ数を取得
    const pageCount = Math.ceil(orders.length / pageSize)

    // 現在のpageIndexが新しい最大ページ数を超えていないか確認
    if (pageIndex > pageCount - 1) {
      setPageIndex(pageCount - 1)
      if (pageCount === 0) {
        setPageIndex(0)
      }
    }
  }, [orders, pageSize])

  // ordersの更新に伴って、モーダルが表示されている場合はtargetOrdersを再計算
  useEffect(() => {
    if (isShowEdit && orderId) {
      const updatedTargetOrders = orders.filter(
        (order) => order.order_id === orderId
      )
      setTargetOrderList(createOrderList(updatedTargetOrders))
      setTargetOrders(updatedTargetOrders)
    }
  }, [orders, isShowEdit, orderId])
  // すべてのチェックボックスをクリアする関数
  const handleClearCheckboxes = () => {
    setOrders((prevOrders) =>
      prevOrders.map((order) => ({ ...order, isChecked: false }))
    )
  }

  // キーワードフィルタをクリアする関数
  const handleClearFilter = () => {
    setGlobalFilter('') // フィルタをリセット
    // 初期の注文データを復元する。isChecked を明示的に持たせないと
    // 行オブジェクトの形が他の経路と食い違う(undefinedになる)
    setOrders(initialOrders.map((order) => ({ ...order, isChecked: false })))
    setColumnFilters([]) // カラムフィルタをリセット
  }

  return (
    <div className={classes.root}>
      <div className={classes.menu}>
        <div className={classes.left}>
          <ColorButtons
            label={'クリア'}
            color="primary"
            onClick={() => {
              handleClearCheckboxes()
              handleClearFilter()
            }}
            width={90}
            height={32}
            bottom={0}
          />

          <div className={classes.filter}>
            <input
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="キーワード..."
            />
          </div>
        </div>
        <Pagination
          table={table}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
        />
      </div>
      <div
        className={classes.container}
        style={{ direction: columnResizeDirection }}
      >
        <table
          className={classes.table}
          style={{ width: table.getTotalSize() }} // テーブルの幅を設定
        >
          <thead className={classes.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    onClick={
                      header.column.getCanSort()
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    style={{
                      cursor: header.column.getCanSort()
                        ? 'pointer'
                        : 'default',
                      width: header.getSize(),
                      minWidth: header.column.columnDef.minSize,
                      maxWidth: header.column.columnDef.maxSize,
                      position: 'relative',
                    }}
                  >
                    <div className={classes.headerCell}>
                      <div
                        className={classes.headerContent}
                        onClick={
                          header.column.getCanSort()
                            ? header.column.getToggleSortingHandler()
                            : undefined
                        }
                      >
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                        {header.column.getCanSort() &&
                          (header.column.getIsSorted() === 'asc' ? (
                            <TiArrowSortedUp />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <TiArrowSortedDown />
                          ) : (
                            <TiArrowUnsorted />
                          ))}
                      </div>
                      {header.column.getCanFilter() && (
                        <div className={classes.headerFilter}>
                          <input
                            value={header.column.getFilterValue() || ''}
                            onChange={(e) =>
                              header.column.setFilterValue(e.target.value)
                            }
                            placeholder={`フィルタ...`}
                            className={classes.filterInput}
                            onClick={(e) => e.stopPropagation()} // ここを追加
                          />
                        </div>
                      )}
                    </div>
                    {header.column.getCanResize() && (
                      <div
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`${classes.resizer} ${
                          header.column.getIsResizing()
                            ? classes.isResizing
                            : ''
                        }`}
                        style={{
                          transform:
                            columnResizeMode === 'onEnd' &&
                            header.column.getIsResizing()
                              ? `translateX(${
                                  (columnResizeDirection === 'rtl' ? -1 : 1) *
                                  (table.getState().columnSizingInfo
                                    .deltaOffset ?? 0)
                                }px)`
                              : '',
                        }}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={
                  row.original.isChecked
                    ? classes.checkedRow
                    : classes.normalRow
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                      minWidth: cell.column.columnDef.minSize,
                      maxWidth: cell.column.columnDef.maxSize,
                    }}
                    // className={
                    //   cell.column.id === "status" &&
                    //   (row.original.status === ORDER_PLACED
                    //     ? classes.placed
                    //     : row.original.status === ORDER_CONFIRMED
                    //     ? classes.confirmed
                    //     : row.original.satus === ORDER_RECEIVED
                    //     ? classes.received
                    //     : classes.canceled)
                    // }
                  >
                    <>
                      {cell.column.id === 'status' ? (
                        <div
                          className={`${classes.statusIcon} ${
                            cell.column.id === 'status' &&
                            (row.original.status === ORDER_PLACED
                              ? classes.placed
                              : row.original.status === ORDER_CONFIRMED
                              ? classes.confirmed
                              : row.original.status === ORDER_RECEIVED
                              ? classes.received
                              : classes.canceled)
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </div>
                      ) : (
                        <>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </>
                      )}
                    </>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {isShowEdit && orderId && targetOrders?.length > 0 && (
        <EditOrderModal
          orderId={orderId}
          orderUid={orderUid}
          setIsShow={setIsShowEdit}
          onClose={handleClose}
          targetOrderList={targetOrderList}
        />
      )}
    </div>
  )
}

export default TableArea
